/*
 * Deferred carousel cards.
 *
 * Product carousels (sections/product-list.liquid) render every product in their
 * collection - up to 50 cards each, ~200 on the SALE page - although a shopper sees two
 * to four per carousel until they swipe. Each card is ~90 elements, six custom elements
 * and a quick-add form, and third-party scripts (Snapmint EMI, the Facebook pixel,
 * Shopify's pixel manager) walk every one of them during load.
 *
 * The section emits cards after the first four inside <template data-jnorss-deferred>.
 * Template content is inert: parsed, but never styled, laid out, upgraded or scanned. This
 * script swaps it in, per carousel, only as far as the shopper can actually reach:
 *   - a few more cards in idle time once the carousel is within half a screen;
 *   - then, while they touch or scroll it, always AHEAD cards past the last visible one.
 *
 * Cards used to be swapped in six at a time, synchronously, on the first touch, and the
 * rest of the carousel (up to 46 cards) right after - so the swipe that asked for them was
 * the one that stuttered, and the page kept growing by thousands of elements nobody saw.
 * Now each swap runs as its own short task with a time budget, so touch and scroll input
 * are handled between them.
 *
 * The slide elements themselves are always rendered, so each carousel's slide count,
 * width, arrows and scroll snapping are exactly what they were.
 *
 * Grids ([data-jnorss-deferred-grid]) keep every cell and hold only the card inside the
 * <template>. A cell is filled once it is within a screen and a half of the viewport, in
 * the same short budgeted tasks, so scrolling never reaches an empty cell and a page of
 * 50-product grids no longer builds ~180 cards before the shopper can scroll.
 */
(function () {
  'use strict';

  if (window.JnorssDeferredSlides) return;

  var SELECTOR = 'template[data-jnorss-deferred]';
  var NEAR_BATCH = 4;
  // A fling on a phone travels three to six cards; keep that many ready past the edge.
  var AHEAD = 6;
  // Longest a single swap task may run before handing the main thread back.
  var BUDGET_MS = 6;
  var INTERACTIONS = ['touchstart', 'pointerdown', 'focusin', 'wheel', 'keydown'];

  var now =
    window.performance && typeof window.performance.now === 'function'
      ? function () {
          return window.performance.now();
        }
      : function () {
          return Date.now();
        };

  var raf =
    typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame.bind(window)
      : function (callback) {
          return window.setTimeout(callback, 16);
        };

  var idle =
    typeof window.requestIdleCallback === 'function'
      ? function (callback) {
          return window.requestIdleCallback(callback, { timeout: 400 });
        }
      : function (callback) {
          return window.setTimeout(callback, 16);
        };

  // A new task, queued behind any pending input, without setTimeout's 4 ms clamp.
  var task = (function () {
    if (window.scheduler && typeof window.scheduler.postTask === 'function') {
      return function (callback) {
        window.scheduler.postTask(callback, { priority: 'user-visible' });
      };
    }
    if (typeof window.MessageChannel === 'function') {
      var channel = new window.MessageChannel();
      var queue = [];
      channel.port1.onmessage = function () {
        var callback = queue.shift();
        if (callback) callback();
      };
      return function (callback) {
        queue.push(callback);
        channel.port2.postMessage(0);
      };
    }
    return function (callback) {
      window.setTimeout(callback, 0);
    };
  })();

  // Snapmint adds its EMI line to the cards it finds at load and re-scans `.product-card`
  // on scroll. Ask it for the new cards once a burst of swaps has settled, in idle time, so a
  // card never stays without the line yet the scan never lands in the middle of a swipe.
  var emiTimer = null;

  function scanEmi() {
    try {
      if (typeof window.collectionList === 'function') window.collectionList('.product-card');
    } catch (error) {
      /* Snapmint still picks the cards up on the next scroll. */
    }
  }

  function refreshEmi() {
    if (emiTimer) window.clearTimeout(emiTimer);
    emiTimer = window.setTimeout(function () {
      emiTimer = null;
      idle(scanEmi);
    }, 120);
  }

  function stateOf(root) {
    if (root.__jnorssDeferred) return root.__jnorssDeferred;

    var scroller = root.querySelector('slideshow-slides');
    var state = {
      root: root,
      scroller: scroller,
      slides: scroller ? scroller.children : root.querySelectorAll('slideshow-slide'),
      next: 0,
      target: 0,
      pitch: 0,
      urgent: false,
      queued: false,
      idleQueued: false,
      frame: false
    };

    root.__jnorssDeferred = state;
    return state;
  }

  function swapSlide(slide) {
    var template = slide && slide.firstElementChild;
    while (template && !(template.localName === 'template' && template.hasAttribute('data-jnorss-deferred'))) {
      template = template.nextElementSibling;
    }
    if (!template || !template.parentNode) return false;
    template.parentNode.replaceChild(template.content, template);
    return true;
  }

  function work(state) {
    var slides = state.slides;
    var end = Math.min(slides.length, state.target);
    var started = now();
    var swapped = false;

    while (state.next < end) {
      if (swapSlide(slides[state.next])) swapped = true;
      state.next += 1;
      if (now() - started > BUDGET_MS) break;
    }

    if (swapped) refreshEmi();
    if (state.next < end) schedule(state);
    else state.urgent = false;
  }

  function schedule(state) {
    if (state.urgent) {
      if (state.queued) return;
      state.queued = true;
      task(function () {
        state.queued = false;
        work(state);
      });
      return;
    }

    if (state.idleQueued || state.queued) return;
    state.idleQueued = true;
    idle(function () {
      state.idleQueued = false;
      work(state);
    });
  }

  // Makes sure the first `count` slides of a carousel hold live cards.
  function hydrateTo(state, count, urgent) {
    if (count <= state.target && !(urgent && state.next < state.target)) return;
    if (count > state.target) state.target = count;
    if (urgent) state.urgent = true;
    schedule(state);
  }

  // Distance from one slide's start to the next, gap included. Slides keep their size
  // whether or not their card is live, so this is measured once per carousel and size.
  function pitchOf(state) {
    if (state.pitch) return state.pitch;
    var slides = state.slides;
    if (slides.length > 1) state.pitch = Math.abs(slides[1].offsetLeft - slides[0].offsetLeft);
    if (!state.pitch && slides.length) state.pitch = slides[0].offsetWidth;
    return state.pitch;
  }

  // Number of slides up to and including the last one in view.
  function reach(state) {
    var scroller = state.scroller;
    var pitch = pitchOf(state);
    if (!scroller || !pitch) return state.slides.length;
    return Math.ceil((Math.abs(scroller.scrollLeft) + scroller.clientWidth) / pitch);
  }

  function follow(state) {
    if (state.frame) return;
    state.frame = true;
    raf(function () {
      state.frame = false;
      hydrateTo(state, reach(state) + AHEAD, true);
    });
  }

  function arm(state) {
    var root = state.root;

    function onInteract() {
      follow(state);
    }

    for (var i = 0; i < INTERACTIONS.length; i += 1) {
      root.addEventListener(INTERACTIONS[i], onInteract, { capture: true, passive: true });
    }

    // scroll does not bubble; the carousel's own scroller reports swipes, arrow buttons and
    // programmatic scrolling alike. Slides use content-visibility: auto, so Snapmint reads an
    // empty price from a card that was still off screen when it scanned and skips it; scan
    // again once the swipe settles, so the cards it brought into view get their EMI line.
    if (state.scroller) {
      state.scroller.addEventListener(
        'scroll',
        function () {
          follow(state);
          refreshEmi();
        },
        { passive: true }
      );
    }
  }

  var observer =
    typeof window.IntersectionObserver === 'function'
      ? new window.IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              observer.unobserve(entry.target);
              var state = stateOf(entry.target);
              idle(function () {
                hydrateTo(state, reach(state) + NEAR_BATCH, false);
              });
            });
          },
          { rootMargin: '50% 0px' }
        )
      : null;

  // On touch devices Snapmint scans for new cards only at load, and it skips any card whose
  // slide was still off screen then: content-visibility: auto leaves the price unrendered, so
  // it reads an empty price. Without another scan the EMI line was added on the first swipe,
  // growing the carousel by ~50px under the shopper's eyes. The price becomes readable once
  // the carousel is ~100px into the viewport, while the price row is still below the fold,
  // so scan as each carousel enters and again as more of it shows.
  var emiObserver =
    typeof window.IntersectionObserver === 'function'
      ? new window.IntersectionObserver(
          function (entries) {
            for (var i = 0; i < entries.length; i += 1) {
              if (entries[i].isIntersecting) {
                // Two frames let the slides that just became relevant render first.
                raf(function () {
                  raf(scanEmi);
                });
                return;
              }
            }
          },
          { threshold: [0, 0.3, 0.6] }
        )
      : null;

  var gridQueue = [];
  var gridQueued = false;

  function gridWork() {
    gridQueued = false;
    var started = now();
    var swapped = false;

    while (gridQueue.length) {
      if (swapSlide(gridQueue.shift())) swapped = true;
      if (now() - started > BUDGET_MS) break;
    }

    if (swapped) refreshEmi();
    if (gridQueue.length) gridSchedule();
  }

  function gridSchedule() {
    if (gridQueued) return;
    gridQueued = true;
    task(gridWork);
  }

  var gridObserver =
    typeof window.IntersectionObserver === 'function'
      ? new window.IntersectionObserver(
          function (entries) {
            for (var i = 0; i < entries.length; i += 1) {
              if (!entries[i].isIntersecting) continue;
              gridObserver.unobserve(entries[i].target);
              gridQueue.push(entries[i].target);
            }
            if (gridQueue.length) gridSchedule();
          },
          { rootMargin: '150% 0px' }
        )
      : null;

  function deferredCells(root) {
    var cells = [];
    var templates = root.querySelectorAll(SELECTOR);
    for (var i = 0; i < templates.length; i += 1) cells.push(templates[i].parentNode);
    return cells;
  }

  function registerGrid(root) {
    var cells = deferredCells(root);
    if (emiObserver) emiObserver.observe(root);
    for (var i = 0; i < cells.length; i += 1) {
      if (gridObserver) gridObserver.observe(cells[i]);
      else gridQueue.push(cells[i]);
    }
    if (!gridObserver) gridSchedule();
  }

  function register(root) {
    if (root.__jnorssRegistered || !root.querySelector(SELECTOR)) return;
    root.__jnorssRegistered = true;

    if (root.hasAttribute('data-jnorss-deferred-grid')) {
      registerGrid(root);
      return;
    }

    var state = stateOf(root);
    arm(state);
    if (emiObserver) emiObserver.observe(root);
    if (observer) observer.observe(root);
    else hydrateTo(state, reach(state) + NEAR_BATCH, false);
  }

  function scan() {
    var roots = document.querySelectorAll('[data-jnorss-deferred-root]');
    for (var i = 0; i < roots.length; i += 1) register(roots[i]);
  }

  function hydrateAll(root) {
    if (root.hasAttribute('data-jnorss-deferred-grid')) {
      var cells = deferredCells(root);
      for (var i = 0; i < cells.length; i += 1) {
        if (gridObserver) gridObserver.unobserve(cells[i]);
        gridQueue.push(cells[i]);
      }
      gridSchedule();
      return;
    }
    var state = stateOf(root);
    hydrateTo(state, state.slides.length, true);
  }

  window.JnorssDeferredSlides = { scan: scan, hydrateAll: hydrateAll };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();

  // Slide widths change with the viewport; measure again on the next interaction.
  window.addEventListener(
    'resize',
    function () {
      var roots = document.querySelectorAll('[data-jnorss-deferred-root]');
      for (var i = 0; i < roots.length; i += 1) {
        if (roots[i].__jnorssDeferred) roots[i].__jnorssDeferred.pitch = 0;
      }
    },
    { passive: true }
  );

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', scan);
})();
